/**
 * Light, dark, or whatever the device says — for the CenoX workspace only.
 *
 * The preference is a per-browser convenience, so it lives in localStorage and
 * every read and write tolerates storage being unavailable (a private window,
 * blocked site data): the answer then is simply "system". CenoX Business does
 * not read this; its screens stay light.
 */

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const KEY = 'cenox.theme';
const QUERY = '(prefers-color-scheme: dark)';

function stored(): ThemePreference {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function deviceIsDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(QUERY).matches;
}

/** The order the toggle steps through. */
export const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

export function useCenoxTheme(): {
  preference: ThemePreference;
  dark: boolean;
  setPreference: (next: ThemePreference) => void;
} {
  const [preference, setState] = useState<ThemePreference>(stored);
  const [systemDark, setSystemDark] = useState(deviceIsDark);

  // Follow the device while the preference is "system".
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(QUERY);
    const onChange = (): void => setSystemDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setState(next);
    try {
      if (next === 'system') window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, next);
    } catch {
      // Not remembered; still applied for this visit.
    }
  }, []);

  return {
    preference,
    dark: preference === 'dark' || (preference === 'system' && systemDark),
    setPreference,
  };
}
